import { Link } from "@tanstack/react-router";
import { ArrowUpRight, QrCode, Plus, Minus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import heroDesktop from "@/assets/hero-crowd-desktop.webp";
import heroMobile from "@/assets/hero-crowd-mobile.webp";
import weddingImg from "@/assets/wedding.jpg";
import setupColumnas from "@/assets/setup-columnas.webp";
import setupMonolito from "@/assets/setup-monolito.webp";
import setupPortico from "@/assets/setup-portico.webp";
import setupTriangulo from "@/assets/setup-triangulo.webp";
import setupArco from "@/assets/setup-arco.webp";
import setupColumnasMovil from "@/assets/setup-columnas-movil.webp";
import setupMonolitoMovil from "@/assets/setup-monolito-movil.webp";
import setupPorticoMovil from "@/assets/setup-portico-movil.webp";
import setupTrianguloMovil from "@/assets/setup-triangulo-movil.webp";
import setupArcoMovil from "@/assets/setup-arco-movil.webp";
import eventsDesktop from "@/assets/events-montaje-desktop.webp";
import eventsMobile from "@/assets/events-montaje-mobile.webp";
import plazaDesktop from "@/assets/plaza-noche-desktop.webp";
import plazaMobile from "@/assets/plaza-noche-mobile.webp";
import { BrandLogo } from "@/components/BrandLogo";
import { useReveal } from "@/hooks/use-reveal";

/* ------------------------------------------------------------------ */
/*  HERO                                                              */
/*  Imagen a sangre, texto abajo a la izquierda. El degradado es       */
/*  funcional (legibilidad), no decorativo: arriba convergen los       */
/*  haces y es la zona más brillante de la foto.                       */
/* ------------------------------------------------------------------ */
export function Hero() {
  return (
    <section className="relative isolate flex min-h-[100dvh] flex-col justify-end overflow-hidden bg-onyx">
      <picture>
        {/* La vertical es una toma propia en 9:16, no un recorte de la
            horizontal: se ve al DJ en cabina con el brazo en alto y el arco
            entero por encima, que en un recorte central se perdía. */}
        <source media="(max-width: 767px)" srcSet={heroMobile} />
        <img
          src={heroDesktop}
          alt="Público con los brazos en alto frente a un escenario con arco de cabezas móviles y haces de luz atravesando el humo"
          width={2000}
          height={1116}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        />
      </picture>

      {/* Máscara de legibilidad, no decoración. En móvil el titular ocupa tres
          líneas y sube hasta la zona iluminada, así que ahí la base es más
          opaca y llega más arriba. Medido: sin ella, el 13 % de los píxeles
          detrás del rojo baja de 3:1. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(to_top,rgb(5_5_5/0.96)_0%,rgb(5_5_5/0.92)_30%,rgb(5_5_5/0.78)_48%,rgb(5_5_5/0.34)_68%,transparent_88%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%)] md:bg-[linear-gradient(to_top,rgb(5_5_5/0.94)_0%,rgb(5_5_5/0.74)_20%,rgb(5_5_5/0.3)_46%,transparent_70%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%),linear-gradient(to_right,rgb(5_5_5/0.5)_0%,transparent_58%)]"
      />

      <div className="mx-auto w-full max-w-7xl px-5 pb-14 lg:px-8 lg:pb-20">
        <h1 className="display-xl hero-in max-w-4xl text-bone">
          EVENTOS QUE <span className="text-neon">SE SIENTEN.</span>
        </h1>
        <p
          className="hero-in mt-7 max-w-xl text-lg leading-relaxed text-bone/85 lg:text-xl"
          style={{ "--reveal-delay": "120ms" } as React.CSSProperties}
        >
          DJ, sonido, iluminación y producción para bodas, ayuntamientos y eventos que quieren
          volverse inolvidables.
        </p>
        <div className="hero-in mt-9" style={{ "--reveal-delay": "240ms" } as React.CSSProperties}>
          <Link
            to="/contacto"
            className="group inline-flex items-center gap-2 bg-primary-strong px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
          >
            Consultar disponibilidad
            <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  VERTICALES                                                        */
/*  Tres líneas, tres pesos distintos. Weddings ocupa el doble porque  */
/*  es donde más se invierte. El alcance de cada una sale del          */
/*  inventario documentado; no se inventan servicios ni packs.         */
/* ------------------------------------------------------------------ */
const VERTICALES = [
  {
    name: "Weddings",
    img: weddingImg,
    alt: "Pareja bailando en una boda de noche bajo guirnaldas de bombillas",
    desc: "Cóctel, comida o cena y fiesta. Con preparación musical previa con la pareja y un portal privado para dejarlo todo escrito.",
    spec: "Experience · Signature · Festival",
    href: "/bodas",
    feature: true,
  },
  {
    name: "Events",
    img: eventsDesktop,
    alt: "Sala montada antes de una fiesta: cabina de madera, dos torres con cabezas móviles y haces ámbar sobre la pista vacía",
    desc: "Cumpleaños, comuniones, bautizos, cenas de empresa, inauguraciones y salas.",
    spec: "Producción a medida",
    href: "/eventos",
    feature: false,
  },
  {
    name: "Public",
    // Sustituye a la banda roja que sostenía la ficha mientras no había foto
    // propia de plaza. Esta no choca con las otras dos de la retícula: noche
    // cerrada, escenario de calle y fachada bañada en rojo — no guirnaldas.
    img: plazaDesktop,
    alt: "Plaza de pueblo de noche con la fachada del ayuntamiento iluminada en rojo, escenario de calle al fondo y público de pie",
    desc: "Ayuntamientos, ferias, fiestas populares y actos institucionales.",
    spec: "Recinto abierto o cerrado",
    href: "/public",
    feature: false,
  },
];

export function Verticales() {
  // Escalonado: comunica jerarquía. Weddings entra primero porque pesa el doble.
  const ref = useReveal<HTMLElement>({ step: 90 });

  return (
    <section ref={ref} className="border-b border-border">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="reveal max-w-3xl">
          <h2 className="display-lg">
            Cambia el sitio, cambia el <span className="text-primary">montaje</span>.
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Una boda puede sonar a festival, y muchas lo hacen. Lo que no se puede repetir es el
            montaje: un patio, una plaza y una sala piden equipos distintos. Por eso el trabajo se
            reparte en tres líneas, cada una dimensionada sobre el espacio real y el aforo.
          </p>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-12 lg:mt-20">
          {VERTICALES.map((v) => {
            return (
              <Link
                key={v.name}
                to={v.href}
                className={`reveal group relative isolate flex overflow-hidden bg-onyx ${
                  v.feature
                    ? "min-h-[26rem] md:col-span-7 md:row-span-2 md:min-h-[38rem]"
                    : "min-h-[20rem] md:col-span-5 md:min-h-[18.5rem]"
                }`}
              >
                <img
                  src={v.img}
                  alt={v.alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 -z-10 h-full w-full object-cover object-center transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                />
                {/* La tarjeta corta necesita más máscara: el texto ocupa casi
                    toda su altura y por debajo puede pasar una zona clara. */}
                <div
                  aria-hidden
                  className={`absolute inset-0 -z-10 ${
                    v.feature
                      ? "bg-[linear-gradient(to_top,rgb(5_5_5/0.92)_0%,rgb(5_5_5/0.55)_38%,rgb(5_5_5/0.15)_72%)]"
                      : "bg-[linear-gradient(to_top,rgb(5_5_5/0.94)_0%,rgb(5_5_5/0.8)_50%,rgb(5_5_5/0.5)_85%,rgb(5_5_5/0.3)_100%)]"
                  }`}
                />

                <div className="mt-auto flex w-full flex-col gap-3 p-6 lg:p-8">
                  <span className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-neon">
                    {v.spec}
                  </span>
                  <h3
                    className={`font-display uppercase leading-[0.9] tracking-[-0.03em] text-bone ${
                      v.feature ? "text-5xl lg:text-7xl" : "text-4xl lg:text-5xl"
                    }`}
                  >
                    {v.name}
                  </h3>
                  <p
                    className={`text-bone/75 ${
                      v.feature ? "max-w-md text-base lg:text-lg" : "max-w-sm text-sm"
                    }`}
                  >
                    {v.desc}
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-bone">
                    Ver la línea
                    <ArrowUpRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>

                {/* Filete que crece al pasar por encima. Marca el foco. */}
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 bg-primary transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100"
                />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  CIFRAS                                                            */
/*  Fondo papel: es el acto del rider dentro del recorrido de la       */
/*  marca (la noche, el rider, el cierre).                             */
/* ------------------------------------------------------------------ */

/**
 * Bodas: +50, confirmado por Juan el 7 sep 2026.
 * Eventos: +200, confirmado por Juan el 19 oct 2026.
 *
 * La de años sigue PENDIENTE DE CONFIRMAR: es una cifra de partida
 * inventada y ahora mismo está publicada. El sistema de marca prohíbe
 * publicar cifras sin verificar (../marketing/social/brand-system/susiedad-social-design-system.md §9).
 * Sustituir por las reales o retirar la fila.
 */
const CIFRAS: { valor: string; etiqueta: string; nota: string }[] = [
  { valor: "+200", etiqueta: "Eventos", nota: "Bodas, ferias, salas y empresas" },
  { valor: "+50", etiqueta: "Bodas", nota: "Cóctel, comida o cena y fiesta" },
  { valor: "4", etiqueta: "Años en cabina", nota: "Sin subcontratar la sesión" },
];

export function Cifras() {
  // Barrido lateral: la lectura es la de un instrumento midiendo, no un fundido.
  const ref = useReveal<HTMLElement>({ step: 70, variant: "wipe" });

  return (
    <section ref={ref} className="border-b border-border bg-bone dark:bg-secondary">
      {/* La retícula va sobre el contenedor, no sobre la sección: así sus doce
          columnas son las mismas a las que se alinea el contenido y la textura
          mide algo en lugar de sólo decorar. */}
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid-plan py-24 lg:py-32">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.35fr] lg:gap-20">
            <div className="reveal">
              <h2 className="display-lg">No es el primer evento.</h2>
              <p className="mt-6 max-w-sm text-lg leading-relaxed text-muted-foreground">
                Equipo propio, montaje y desmontaje incluidos, y una sesión preparada antes de
                llegar al sitio.
              </p>
              <Link
                to="/contacto"
                className="mt-8 inline-flex items-center gap-2 border border-foreground px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-200 hover:bg-foreground hover:text-background active:translate-y-px"
              >
                Consultar disponibilidad <ArrowUpRight className="size-4" />
              </Link>
            </div>

            <dl className="flex flex-col justify-center">
              {CIFRAS.map((c) => (
                <div
                  key={c.etiqueta}
                  className="reveal flex flex-col gap-1 border-t border-foreground/15 py-6 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8"
                >
                  <dt className="order-2 sm:order-1">
                    <span className="font-display text-xl tracking-tight">{c.etiqueta}</span>
                    <span className="mt-1 block font-mono text-[0.62rem] uppercase tracking-[0.18em] text-muted-foreground">
                      {c.nota}
                    </span>
                  </dt>
                  <dd className="order-1 font-display text-6xl leading-none tracking-[-0.03em] tabular-nums sm:order-2 lg:text-7xl">
                    <span className="reveal-wipe-target inline-block">{c.valor}</span>
                  </dd>
                </div>
              ))}
              <div className="reveal spec-row border-foreground/15">
                <span className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  Territorio
                </span>
                <span className="font-display text-lg tracking-tight">Málaga · Toda España</span>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  WEDDINGS                                                          */
/*  Hereda el titular que antes llevaba el hero.                       */
/* ------------------------------------------------------------------ */
const PORTAL_GRUPOS: { titulo: string; items: string[] }[] = [
  {
    titulo: "Lo que suena",
    items: ["Canciones imprescindibles", "Canciones favoritas", "Estilos musicales"],
  },
  {
    titulo: "Lo que no suena",
    items: ["Canciones prohibidas"],
  },
  {
    titulo: "El guion de la noche",
    items: ["Momentos especiales", "Peticiones adicionales", "Información del evento"],
  },
];

export function Weddings() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-24 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-8 lg:py-32">
        <div>
          <h2 className="display-lg">
            La música adecuada, en el <span className="text-primary">momento exacto.</span>
          </h2>
          <p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground">
            La sesión se prepara con vosotros antes del día: qué suena en el cóctel, qué suena en la
            comida o la cena, y con qué arranca la fiesta.
          </p>

          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {PORTAL_GRUPOS.map((g) => (
              <div key={g.titulo} className="border-t border-primary pt-4">
                <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-foreground">
                  {g.titulo}
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  {g.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="border-t border-border pt-4">
              <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Dónde se rellena
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                En un portal privado, a vuestro ritmo, con el detalle que queráis.
              </p>
            </div>
          </div>

          <Link
            to="/contacto"
            className="mt-12 inline-flex items-center gap-2 bg-primary-strong px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
          >
            Consultar disponibilidad <ArrowUpRight className="size-4" />
          </Link>
        </div>

        <div className="relative">
          <div className="aspect-[4/5] overflow-hidden bg-onyx lg:sticky lg:top-28">
            <img
              src={weddingImg}
              alt="Pareja bailando al aire libre en una boda de noche"
              width={1200}
              height={1400}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  GUEST QR                                                          */
/*  Fuera de la landing. Sigue viva en /bodas, que es donde interesa.  */
/* ------------------------------------------------------------------ */
export function GuestQR() {
  const sample = [
    { t: "Blinding Lights", a: "The Weeknd", v: 42 },
    { t: "Dance the Night", a: "Dua Lipa", v: 38 },
    { t: "One Kiss", a: "Calvin Harris", v: 31 },
    { t: "Suavemente", a: "Elvis Crespo", v: 27 },
  ];
  return (
    <section className="bg-onyx text-bone">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 lg:grid-cols-[1fr_1.1fr] lg:gap-20 lg:px-8 lg:py-32">
        <div className="flex flex-col justify-center">
          <h2 className="display-lg text-bone">
            Tus invitados también forman parte de <span className="text-neon">la música</span>.
          </h2>
          <p className="mt-6 max-w-xl text-lg text-bone/70">
            Un QR en la mesa. Sin cuentas. Sin apps. Los invitados proponen canciones, votan las
            favoritas y ven el ranking en tiempo real.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-bone/80">
            {[
              "Escanean el código QR",
              "Proponen canciones sin registro",
              "Votan las que quieren escuchar",
              "Tú mantienes el control y se modera todo",
            ].map((x) => (
              <li key={x} className="flex items-center gap-3">
                <span className="size-1.5 rounded-full bg-neon" /> {x}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center justify-center">
          <div className="relative w-full max-w-sm">
            {/* Maqueta del portal de invitados */}
            <div className="relative aspect-[9/18] w-full rounded-[2.5rem] border-4 border-bone/15 bg-onyx p-3 shadow-2xl">
              <div className="h-full w-full overflow-hidden rounded-[2rem] bg-background text-foreground">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <BrandLogo className="h-3 w-auto" />
                  <span className="font-mono text-[9px] uppercase tracking-widest text-primary">
                    ● Live
                  </span>
                </div>
                <div className="p-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    Boda · Laura + Miguel
                  </p>
                  <p className="mt-1 font-display text-base leading-tight">
                    ¿Qué canción no puede faltar?
                  </p>
                  <div className="mt-4 space-y-2">
                    {sample.map((s, i) => (
                      <div
                        key={s.t}
                        className="flex items-center gap-2 border border-border p-2 text-[11px]"
                      >
                        <span className="font-mono text-primary">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold">{s.t}</p>
                          <p className="truncate text-muted-foreground">{s.a}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-display text-sm text-primary">{s.v}</p>
                          <p className="font-mono text-[8px] uppercase text-muted-foreground">
                            votos
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* QR flotante */}
            <div className="absolute -right-4 -top-4 hidden rounded-md bg-bone p-2 shadow-xl sm:block">
              <div className="grid h-24 w-24 grid-cols-8 gap-px bg-bone p-1">
                {Array.from({ length: 64 }).map((_, i) => (
                  <div
                    key={i}
                    className={`aspect-square ${(i * 17 + Math.floor(i / 8) * 7) % 5 < 2 ? "bg-onyx" : "bg-transparent"}`}
                  />
                ))}
              </div>
              <div className="mt-1 flex items-center justify-center gap-1 text-onyx">
                <QrCode className="size-3" />
                <span className="font-mono text-[8px] uppercase tracking-widest">Escanear</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PUBLIC · APERTURA                                                 */
/*  Mismo arranque que /eventos: la foto a sangre ocupa la ventana y   */
/*  el texto se apoya abajo a la izquierda, sobre el público en        */
/*  silueta, que es la zona oscura del encuadre.                       */
/* ------------------------------------------------------------------ */
export function PublicoHero() {
  return (
    <section className="relative isolate flex min-h-[100dvh] flex-col justify-end overflow-hidden bg-onyx">
      <picture>
        <source media="(max-width: 767px)" srcSet={plazaMobile} />
        <img
          src={plazaDesktop}
          alt="Plaza de pueblo de noche: la fachada del ayuntamiento y la torre del reloj iluminadas en rojo, el escenario con los haces encendidos y el público de pie delante"
          width={2000}
          height={1117}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        />
      </picture>

      {/* La fachada de la izquierda va bañada en rojo y es lo que queda detrás
          del texto, así que la base tapa más que en la portada. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(to_top,rgb(5_5_5/0.96)_0%,rgb(5_5_5/0.9)_26%,rgb(5_5_5/0.6)_44%,rgb(5_5_5/0.2)_64%,transparent_86%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%)] lg:bg-[linear-gradient(to_top,rgb(5_5_5/0.95)_0%,rgb(5_5_5/0.86)_22%,rgb(5_5_5/0.52)_40%,rgb(5_5_5/0.14)_62%,transparent_84%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%),linear-gradient(to_right,rgb(5_5_5/0.6)_0%,transparent_58%)]"
      />

      <div className="mx-auto w-full max-w-7xl px-5 pb-14 lg:px-8 lg:pb-20">
        <p className="eyebrow hero-in text-bone/80">SUSIEDAD Public</p>
        <h1
          className="display-lg hero-in mt-5 max-w-2xl text-bone"
          style={{ "--reveal-delay": "80ms" } as React.CSSProperties}
        >
          UNA PLAZA LLENA TIENE SUS <span className="text-neon">REGLAS.</span>
        </h1>
        <p
          className="hero-in mt-7 max-w-xl text-lg leading-relaxed text-bone/85 lg:text-xl"
          style={{ "--reveal-delay": "180ms" } as React.CSSProperties}
        >
          Ferias, fiestas populares y actos institucionales, con el montaje dimensionado sobre el
          aforo declarado.
        </p>
        <div className="hero-in mt-9" style={{ "--reveal-delay": "280ms" } as React.CSSProperties}>
          <Link
            to="/contacto"
            className="group inline-flex items-center gap-2 bg-primary-strong px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
          >
            Consultar disponibilidad
            <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PUBLIC · FICHA                                                    */
/*  Al subir la foto a la apertura, la ficha se queda sin columna de   */
/*  imagen y ocupa el ancho: el texto a la izquierda y las filas       */
/*  técnicas a la derecha. Quien compra aquí lee por expediente.       */
/* ------------------------------------------------------------------ */
const FICHA_PUBLIC: [string, string][] = [
  ["Alcance", "Ferias, fiestas populares y actos institucionales"],
  ["Equipo", "Sonido, iluminación, cabina y personal técnico"],
  ["Montaje", "Montaje, configuración y desmontaje incluidos"],
  ["Recinto", "Abierto o cerrado, dimensionado sobre el aforo real"],
  ["Territorio", "Málaga · Toda España"],
];

export function Publico() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-24 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:px-8 lg:py-32">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="eyebrow">Ficha</p>
          <h2 className="display-lg mt-5">
            El montaje, por <span className="text-primary">escrito</span>.
          </h2>
          <p className="mt-7 max-w-md text-lg leading-relaxed text-muted-foreground">
            Se trabaja con la organización y se respeta la ventana de montaje que marque el
            ayuntamiento o el recinto.
          </p>
          <Link
            to="/contacto"
            className="mt-10 inline-flex items-center gap-2 bg-primary-strong px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
          >
            Consultar disponibilidad <ArrowUpRight className="size-4" />
          </Link>
        </div>

        <dl className="lg:pt-2">
          {FICHA_PUBLIC.map(([k, v]) => (
            <div key={k} className="spec-row py-5">
              <dt className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                {k}
              </dt>
              <dd className="max-w-sm text-right text-base">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  EVENTOS · APERTURA                                                */
/*  Acto "noche" de /eventos, igual que el hero en la portada y la     */
/*  escena a sangre en /bodas. La foto es el argumento de la línea:    */
/*  la sala montada y todavía vacía, que es lo que se compra aquí.     */
/* ------------------------------------------------------------------ */
export function EventosHero() {
  return (
    <section className="relative isolate flex min-h-[100dvh] flex-col justify-end overflow-hidden bg-onyx">
      <picture>
        <source media="(max-width: 767px)" srcSet={eventsMobile} />
        <img
          src={eventsDesktop}
          alt="Sala montada antes de una fiesta: cabina de madera, dos torres con cabezas móviles, puente de iluminación con los haces encendidos y la pista todavía vacía"
          width={1800}
          height={1395}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        />
      </picture>

      {/* Máscara de legibilidad. Arriba porque la cabecera flota sobre el puente
          de luces, que es lo más claro del encuadre; abajo porque el suelo
          devuelve el rojo de las columnas justo donde se apoya el texto. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(to_top,rgb(5_5_5/0.96)_0%,rgb(5_5_5/0.9)_26%,rgb(5_5_5/0.62)_42%,rgb(5_5_5/0.22)_62%,transparent_84%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%)] lg:bg-[linear-gradient(to_top,rgb(5_5_5/0.95)_0%,rgb(5_5_5/0.9)_24%,rgb(5_5_5/0.68)_40%,rgb(5_5_5/0.2)_62%,transparent_84%),linear-gradient(to_bottom,rgb(5_5_5/0.55)_0%,transparent_16%),linear-gradient(to_right,rgb(5_5_5/0.62)_0%,transparent_58%)]"
      />

      <div className="mx-auto w-full max-w-7xl px-5 pb-14 lg:px-8 lg:pb-20">
        {/* La etiqueta va en hueso, no en rojo: a 0,67rem sobre la foto el rojo se
            queda en 2,7:1 y no pasa AA para texto pequeño. Mismo criterio que el
            pie de página. El acento se queda en el titular y en el botón. */}
        <p className="eyebrow hero-in text-bone/80">SUSIEDAD Events</p>
        <h1
          className="display-lg hero-in mt-5 max-w-2xl text-bone"
          style={{ "--reveal-delay": "80ms" } as React.CSSProperties}
        >
          DA IGUAL QUÉ <span className="text-neon">SE CELEBRE.</span>
        </h1>
        <p
          className="hero-in mt-7 max-w-xl text-lg leading-relaxed text-bone/85 lg:text-xl"
          style={{ "--reveal-delay": "180ms" } as React.CSSProperties}
        >
          Llega el equipo, se monta sobre el espacio real y la sala queda lista antes de que entre
          el primer invitado.
        </p>
        <div className="hero-in mt-9" style={{ "--reveal-delay": "280ms" } as React.CSSProperties}>
          <Link
            to="/contacto"
            className="group inline-flex items-center gap-2 bg-primary-strong px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
          >
            Consultar disponibilidad
            <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  CELEBRACIONES                                                     */
/*  Ocupa el hueco de "Mixes recientes", que enseñaba cuatro sesiones   */
/*  inventadas. Composición propia dentro del sitio: cartel. El         */
/*  interpunto es el separador que ya usa la marca ("Cóctel · Comida o  */
/*  cena · Fiesta") llevado a tamaño de display, así que la lista es la  */
/*  pieza gráfica y no hace falta tarjeta ni icono.                     */
/*  El alcance sale de oferta-bodas.md (§EVENTS y §CORPORATE).          */
/* ------------------------------------------------------------------ */
const CELEBRACIONES = [
  "Cumpleaños",
  "Comuniones",
  "Bautizos",
  "Aniversarios",
  "Cenas de empresa",
  "Inauguraciones",
  "Fiestas privadas",
  "Salas",
];

export function Celebraciones() {
  return (
    <section className="border-b border-border bg-bone dark:bg-secondary">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <p className="eyebrow">Alcance</p>
        <h2 className="display-lg mt-5 max-w-3xl">
          Aquí entra <span className="text-primary">cualquier celebración</span>.
        </h2>

        {/* El interpunto va detrás de cada nombre, nunca delante: al plegarse en
            móvil, uno delante convierte el cartel en una lista con topos. */}
        <ul className="mt-12 flex max-w-5xl flex-wrap items-baseline font-display text-2xl uppercase leading-[1.2] tracking-[-0.03em] sm:text-3xl lg:mt-16 lg:text-5xl">
          {CELEBRACIONES.map((c, i) => (
            <li key={c} className="flex items-baseline whitespace-nowrap">
              {c}
              {i < CELEBRACIONES.length - 1 && (
                <span aria-hidden className="px-2.5 text-primary lg:px-4">
                  ·
                </span>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-14 grid gap-10 border-t border-border pt-10 md:grid-cols-[1.3fr_1fr] md:gap-16 lg:mt-16">
          <p className="max-w-xl text-lg leading-relaxed text-muted-foreground">
            Cambia el aforo y cambia el repertorio. El trabajo es el mismo: DJ y preparación
            musical, sonido, iluminación, cabina, montaje y desmontaje, dimensionado sobre el
            espacio y el aforo reales.
          </p>
          <div className="flex flex-col items-start gap-4">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Lo que prepares y no esté en la lista se dimensiona igual.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest transition-colors hover:text-primary"
            >
              Consultar disponibilidad <ArrowUpRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  SETUPS                                                            */
/*  El mismo patio con cinco estructuras. La foto es idéntica en las   */
/*  cinco a propósito: lo único que cambia es el montaje, y ese es el  */
/*  argumento. Por eso la imagen se sustituye en el sitio en vez de    */
/*  ponerse cinco fotos en una retícula, donde no se compara nada.     */
/*                                                                    */
/*  El enganche es un IntersectionObserver con la línea de disparo en  */
/*  el centro de la ventana, no un listener de scroll: sólo un paso    */
/*  puede cruzar esa línea, así que el índice activo nunca empata.     */
/*  Sin JS o con movimiento reducido se sirve la versión apilada, con  */
/*  cada imagen junto a su texto.                                      */
/* ------------------------------------------------------------------ */
type Setup = {
  id: string;
  img: string;
  imgMovil: string;
  nombre: string;
  detalle: string;
  alt: string;
};

const SETUPS: Setup[] = [
  {
    id: "columnas",
    img: setupColumnas,
    imgMovil: setupColumnasMovil,
    nombre: "Columnas",
    detalle:
      "Nada por encima de la pista. Dos columnas de madera a los lados, a juego con la cabina, y el puente de iluminación al fondo.",
    alt: "Patio de noche con dos columnas de madera iluminadas a los lados de la cabina y haces ámbar cruzando la pista vacía",
  },
  {
    id: "monolito",
    img: setupMonolito,
    imgMovil: setupMonolitoMovil,
    nombre: "Monolito",
    detalle:
      "Dos paneles de pantalla se levantan detrás de la cabina formando un monolito, y a los lados quedan dos torres bajas con las cabezas móviles.",
    alt: "El mismo patio con un monolito vertical de dos paneles de pantalla detrás de la cabina y dos torres bajas a los lados",
  },
  {
    id: "portico",
    img: setupPortico,
    imgMovil: setupPorticoMovil,
    nombre: "Pórtico",
    detalle:
      "Una estructura cuadrada enmarca la cabina, con pantalla al fondo y otra en el techo del propio pórtico.",
    alt: "El mismo patio con una estructura cuadrada que enmarca la cabina, una pantalla al fondo y otra en el techo de la estructura",
  },
  {
    id: "triangulo",
    img: setupTriangulo,
    imgMovil: setupTrianguloMovil,
    nombre: "Triángulo",
    detalle:
      "Dos triángulos encajados uno dentro de otro: el de delante reparte las cabezas móviles por las diagonales y el del fondo enmarca la pared que hay detrás de la cabina.",
    alt: "El mismo patio con dos triángulos de estructura encajados, el de delante con cabezas móviles en las diagonales y el del fondo enmarcando la pared tras la cabina",
  },
  {
    id: "arco",
    img: setupArco,
    imgMovil: setupArcoMovil,
    nombre: "Arco",
    detalle:
      "Un aro casi cerrado rodea la cabina, con las barras de luz y las cabezas móviles repartidas por todo el recorrido de la estructura.",
    alt: "El mismo patio con un aro de estructura casi cerrado alrededor de la cabina, con barras de luz y cabezas móviles repartidas por todo el aro",
  },
];

function SetupsCabecera() {
  return (
    <div className="max-w-3xl">
      <p className="eyebrow">Montaje</p>
      <h2 className="display-lg mt-5">
        La estructura también <span className="text-primary">se elige</span>.
      </h2>
      <p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground">
        El patio es el mismo en las cinco. Lo único que cambia es lo que se monta encima, y eso
        cambia la foto de la entrada, del primer baile y del resto de la noche.
      </p>
    </div>
  );
}

function SetupsPie() {
  return (
    <p className="mt-14 border-t border-border pt-6 font-mono text-[0.62rem] uppercase leading-relaxed tracking-[0.22em] text-muted-foreground">
      Simulaciones de montaje. La estructura final se dimensiona sobre el recinto real.
    </p>
  );
}

export function Setups() {
  const [activo, setActivo] = useState(0);
  const [enganchado, setEnganchado] = useState(false);
  const pasos = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // El enganche es sólo de escritorio. En móvil la comparación se hace
    // deslizando el carrusel, que es el gesto natural del pulgar y no pelea con
    // el scroll de la página.
    if (window.matchMedia("(min-width: 1024px)").matches) setEnganchado(true);
  }, []);

  useEffect(() => {
    if (!enganchado) return;
    const observer = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => {
          if (e.isIntersecting) setActivo(Number((e.target as HTMLElement).dataset.indice));
        });
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );
    pasos.current.forEach((p) => p && observer.observe(p));
    return () => observer.disconnect();
  }, [enganchado]);

  return (
    <section className="border-b border-border bg-bone dark:bg-secondary">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <SetupsCabecera />

        {/* MÓVIL Y TABLETA · carrusel de verticales.
            Las fotos son 9:16 grabadas aparte, no recortes del horizontal, y se
            deslizan con el pulgar. La versión anclada de escritorio en una
            pantalla estrecha dejaba el rótulo pegado encima del titular
            siguiente y se leía como un fallo. */}
        <div className="mt-12 lg:hidden">
          <ul className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SETUPS.map((s, i) => (
              <li key={s.id} className="w-[78%] shrink-0 snap-center sm:w-[52%]">
                <div className="aspect-[9/16] overflow-hidden bg-onyx">
                  <img
                    src={s.imgMovil}
                    alt={s.alt}
                    width={820}
                    height={1469}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="mt-4 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                  {String(i + 1).padStart(2, "0")} / {String(SETUPS.length).padStart(2, "0")}
                </p>
                <h3 className="mt-2 font-display text-2xl uppercase leading-none tracking-[-0.03em]">
                  {s.nombre}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.detalle}</p>
              </li>
            ))}
          </ul>
          <p className="mt-2 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-muted-foreground">
            Desliza para ver los cinco
          </p>
        </div>

        {/* ESCRITORIO · la foto ocupa el ancho del contenedor.
            Las cinco son el mismo encuadre, así que la comparación sólo funciona
            si la imagen es grande: a 32rem el cambio no se notaba. */}
        <div className="hidden lg:mt-20 lg:block">
          {enganchado ? (
            <>
              <div className="sticky top-0 z-10 flex h-[100dvh] flex-col justify-center pt-16">
                <div className="relative aspect-video w-full overflow-hidden bg-onyx">
                  {SETUPS.map((s, i) => (
                    <img
                      key={s.id}
                      src={s.img}
                      alt={i === activo ? s.alt : ""}
                      width={1600}
                      height={893}
                      loading="lazy"
                      decoding="async"
                      aria-hidden={i === activo ? undefined : true}
                      className={`setup-capa absolute inset-0 h-full w-full object-cover ${
                        i === activo ? "is-activa" : ""
                      }`}
                    />
                  ))}
                </div>

                {/* Ficha bajo la foto, nunca encima: el sistema de marca no deja
                    poner rótulos sobre las fotografías. */}
                <div className="mt-6 grid grid-cols-[auto_1fr] items-start gap-8 border-t border-border pt-5">
                  <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-primary">
                    {String(activo + 1).padStart(2, "0")} / {String(SETUPS.length).padStart(2, "0")}
                  </p>
                  <div className="grid grid-cols-[14rem_1fr] items-baseline gap-8">
                    <h3 className="font-display text-3xl uppercase leading-none tracking-[-0.03em]">
                      {SETUPS[activo].nombre}
                    </h3>
                    <p className="max-w-2xl leading-relaxed text-muted-foreground">
                      {SETUPS[activo].detalle}
                    </p>
                  </div>
                </div>
              </div>

              {/* Motor del recorrido: un tramo por montaje. Van vacíos porque el
                  texto vive en la ficha, que cambia con la foto. */}
              <div aria-hidden>
                {SETUPS.map((s, i) => (
                  <div
                    key={s.id}
                    data-indice={i}
                    ref={(el) => {
                      pasos.current[i] = el;
                    }}
                    className="h-[58vh]"
                  />
                ))}
              </div>
            </>
          ) : (
            <ol className="space-y-16">
              {SETUPS.map((s, i) => (
                <li key={s.id}>
                  <div className="aspect-video overflow-hidden bg-onyx">
                    <img
                      src={s.img}
                      alt={s.alt}
                      width={1600}
                      height={893}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="mt-5 max-w-xl">
                    <span className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-2 font-display text-2xl uppercase leading-none tracking-[-0.03em]">
                      {s.nombre}
                    </h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">{s.detalle}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <SetupsPie />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  CANCIÓN A MEDIDA                                                  */
/*  El único sitio del sitio donde se enseña el trabajo de antes de    */
/*  la boda. El esquema de pistas está dibujado en DOM, no es una      */
/*  captura: así usa la paleta y la tipografía del rider, escala en    */
/*  móvil y el rojo puede significar algo (lo que se ha tocado).       */
/*  Alcance: oferta-bodas.md §Signature, "modificación personalizada   */
/*  de canciones para entradas, bailes y momentos especiales".         */
/* ------------------------------------------------------------------ */
const MOMENTOS = [
  "Entrada al banquete",
  "Primer baile",
  "Baile con la familia",
  "El ramo",
  "La tarta",
  "Entrada a la fiesta",
  "La última canción",
];

/**
 * Pistas del esquema. `bloques` son pares [inicio, ancho] en porcentaje del
 * compás total. Las marcadas `tocada` van en rojo: son las que se han movido,
 * cortado o añadido para esta versión, y la leyenda lo dice.
 */
const PISTAS: { nombre: string; bloques: [number, number][]; tocada?: boolean }[] = [
  {
    nombre: "Hats",
    bloques: [
      [0, 30],
      [32, 26],
      [60, 40],
    ],
  },
  {
    nombre: "Melody",
    bloques: [
      [0, 18],
      [24, 34],
      [60, 40],
    ],
  },
  {
    nombre: "Harp",
    bloques: [
      [18, 12],
      [46, 12],
    ],
  },
  {
    nombre: "Bass",
    bloques: [
      [32, 6],
      [42, 6],
      [52, 6],
      [62, 6],
      [72, 6],
      [82, 6],
    ],
  },
  {
    nombre: "Riser",
    bloques: [
      [26, 6],
      [56, 6],
      [88, 8],
    ],
    tocada: true,
  },
  { nombre: "Violins", bloques: [[0, 22]], tocada: true },
  {
    nombre: "Choral",
    bloques: [
      [60, 18],
      [82, 18],
    ],
    tocada: true,
  },
];

export function CancionAMedida() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="max-w-3xl">
          <p className="eyebrow">Preparación musical</p>
          <h2 className="display-lg mt-5">
            Cada momento tiene su <span className="text-primary">canción exacta</span>.
          </h2>
          <p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Se elige con vosotros, canción por canción. Y si el tema no encaja con el momento, se
            edita: se corta, se alarga, se cambia el tempo o se enlazan dos, para que termine justo
            cuando llegáis al centro de la pista.
          </p>
        </div>

        <div className="mt-16 grid gap-14 lg:mt-20 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <div>
            <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Momentos que se preparan
            </p>
            <ul className="mt-6">
              {MOMENTOS.map((m) => (
                <li key={m} className="border-t border-border py-3.5 text-base">
                  {m}
                </li>
              ))}
            </ul>
            <p className="mt-8 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Los tres que os importen o los siete. Sin límite de canciones modificadas.
            </p>
          </div>

          {/* Esquema de una entrada editada. Cada barra es un elemento que entra
              o sale; las rojas son las que se han tocado para esta versión. */}
          <figure className="m-0">
            <div className="bg-onyx p-5 lg:p-7">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-bone/70">
                  Entrada · versión 3
                </p>
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-bone/65">
                  2 min 41 s
                </p>
              </div>

              <div
                aria-hidden
                className="tick-ruler mt-5 w-full [--foreground:#f5f3f0] opacity-70"
              />

              <div className="mt-4 space-y-2.5">
                {PISTAS.map((p) => (
                  <div key={p.nombre} className="flex items-center gap-4">
                    <span className="w-16 shrink-0 font-mono text-[0.58rem] uppercase tracking-[0.14em] text-bone/55 sm:w-20">
                      {p.nombre}
                    </span>
                    <span className="relative block h-3 flex-1 bg-bone/[0.07]">
                      {p.bloques.map(([x, w]) => (
                        <span
                          key={`${p.nombre}-${x}`}
                          className={`absolute inset-y-0 ${p.tocada ? "bg-primary" : "bg-bone/60"}`}
                          style={{ left: `${x}%`, width: `${w}%` }}
                        />
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <figcaption className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span className="block h-2.5 w-5 bg-primary" /> Lo que se ha tocado para esta
                versión
              </span>
              <span>Esquema de una entrada editada, no una captura de vuestra canción.</span>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  PROCESO                                                           */
/*  Cuatro pasos colgando de una regla de medida.                      */
/* ------------------------------------------------------------------ */
const STEPS: [string, string][] = [
  ["Cuéntame tu evento", "Fecha, lugar y tipo. Sin compromiso y sin plantillas."],
  ["Confirmamos disponibilidad", "Respuesta en menos de 48 h con una propuesta orientativa."],
  ["Diseñamos la propuesta musical", "Portal privado, preferencias reales y momentos clave."],
  ["Hacemos que ocurra", "Montaje, producción y una noche que no se repite."],
];

export function Process() {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <h2 className="display-lg max-w-3xl">
          De la primera llamada a la <span className="text-primary">última canción</span>.
        </h2>

        <div aria-hidden className="tick-ruler mt-14 w-full text-foreground opacity-50 lg:mt-20" />

        <ol className="grid gap-x-8 gap-y-12 pt-8 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="flex flex-col gap-3">
              <span className="font-mono text-xs tabular-nums tracking-[0.22em] text-foreground">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-xl leading-tight tracking-tight">{t}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ                                                               */
/* ------------------------------------------------------------------ */
const FAQS: [string, string][] = [
  [
    "¿Con cuánto tiempo debo reservar?",
    "Idealmente entre 6 y 12 meses antes, sobre todo para bodas en temporada alta. Consúltame igualmente si tu fecha es próxima.",
  ],
  [
    "¿Trabajas fuera de tu ciudad?",
    "Sí. La base está en Málaga y el servicio está disponible en toda España bajo presupuesto.",
  ],
  [
    "¿Podemos elegir canciones?",
    "Por supuesto. Tendrás un portal privado donde indicar imprescindibles, favoritas y estilos preferidos.",
  ],
  [
    "¿Se pueden indicar canciones prohibidas?",
    "Sí. Es una parte clave del portal: nada suena si no queréis que suene.",
  ],
  [
    "¿Los invitados pueden pedir canciones?",
    "Sí, a través de un QR en el evento. Todas las peticiones se moderan.",
  ],
  [
    "¿Cómo funciona el portal privado?",
    "Recibes un enlace personal. Vais rellenando cuando queráis: preferencias, canciones, momentos y documentos.",
  ],
  [
    "¿Incluyes equipo de sonido e iluminación?",
    "Sí. El servicio incluye sonido, iluminación, cabina, montaje y desmontaje. Los efectos como fuego frío, CO₂ o confeti se contratan aparte y se adaptan al espacio.",
  ],
  [
    "¿Cómo se confirma una fecha?",
    "Con la firma del contrato y una reserva. A partir de ahí, la fecha es tuya.",
  ],
];

export function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="border-b border-border bg-bone dark:bg-secondary">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-24 lg:grid-cols-[1fr_1.5fr] lg:gap-20 lg:px-8 lg:py-32">
        <div>
          <h2 className="display-lg">Preguntas frecuentes.</h2>
          <p className="mt-6 text-muted-foreground">
            Si tienes dudas que no aparecen aquí, escríbeme. Respondo yo mismo.
          </p>
        </div>
        <ul className="divide-y divide-border border-y border-border">
          {FAQS.map(([q, a], i) => {
            const isOpen = open === i;
            return (
              <li key={q}>
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full items-start justify-between gap-4 py-5 text-left"
                >
                  <span className="font-display text-lg">{q}</span>
                  <span className="mt-1 inline-flex size-6 flex-none items-center justify-center border border-border">
                    {isOpen ? <Minus className="size-3" /> : <Plus className="size-3" />}
                  </span>
                </button>
                {isOpen && <p className="pb-5 pr-10 text-muted-foreground">{a}</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  CIERRE                                                            */
/* ------------------------------------------------------------------ */
export function FinalCTA() {
  return (
    <section className="bg-onyx text-bone">
      <div className="mx-auto max-w-7xl px-5 py-24 text-center lg:px-8 lg:py-40">
        <BrandLogo variant="monogram" className="mx-auto h-12 w-auto" />
        <h2 className="display-xl mx-auto mt-8 max-w-4xl text-bone">
          Hagamos que tu evento <span className="text-neon">suene diferente.</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-lg text-bone/70">
          Cuéntame la fecha, el lugar y el tipo de evento.
        </p>
        <Link
          to="/contacto"
          className="mt-10 inline-flex items-center gap-2 bg-primary-strong px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px"
        >
          Consultar disponibilidad <ArrowUpRight className="size-4" />
        </Link>
      </div>
    </section>
  );
}
